import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Select } from "antd";
import { SimpleCross, SimplePlus } from "../../assets/icons";
import ButtonLoader from "../../atoms/buttonLoader";
import SuccessSideBar from "./SuccessSideBar";
import { CreateTask, getProjectsData, updateTask } from "../../services/service";
import { DropDown } from "../../assets/icons";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL } from "../../config/api";
import UpgradePromptModal from "./UpgradePromptModal";
import { useSelector } from 'react-redux';
import { selectCurrentPlan } from '../../redux/selectors/authSelectors';
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';

const { Option } = Select;

const CreateTaskSidebar = ({ visible, onClose, projectId, from, taskData=null, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [isSuccessbarVisible, setSuccessbarVisible] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectName, setSelectedProjectName] = useState("");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentPlan = useSelector(selectCurrentPlan);
  const [deletedAttachments, setDeletedAttachments] = useState([]);

  useEffect(() => {
    if (!visible) return; // Don't fetch if sidebar is not visible
    
    if (projectId) {
      setSelectedProject(projectId);
      const fetchProjectDetails = async () => {
        try {
          const response = await getProjectsData('', 1, 1000, "");
          if (response?.success && response.data) {
            const projectDetails = response.data.find(pro => pro.id === projectId);
            // Always set the selected project name when projectId exists
            if (projectDetails) {
              setSelectedProjectName(projectDetails);
            }
            
            if (taskData) {
              formik.setValues({
                taskName: taskData.name || "",
                description: taskData.description || "",
                project: projectDetails?.id || "",
                attachments: []
              });
              
              if (taskData.attachements?.length > 0) {
                setExistingAttachments(taskData.attachements);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching project details:", error);
        }
      };
      fetchProjectDetails();
    } else {
      const fetchProjects = async () => {
        try {
          const response = await getProjectsData("", 1, 100, "");
          if (response?.success) {
            setProjects(response?.data || []);
          }
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      };
      fetchProjects();
    }
  }, [visible, projectId, taskData]);

  const formik = useFormik({
    initialValues: {
      taskName: "",
      description: "",
      attachments: [],
      project: projectId || "",
    },
    validationSchema: Yup.object({
      taskName: Yup.string().required("Task name is required"),
      description: Yup.string().required("Description is required"),
      project: projectId ? Yup.string() : Yup.string().required("Project is required"),
      attachments: Yup.array().of(
        Yup.object({
          file: Yup.mixed()
        })
      )
    }),
    onSubmit: async (values, { resetForm }) => {
      setIsLoading(true);
      try {
        const formData = new FormData();
        formData.append("title", values.taskName);
        formData.append("task_description", values.description);
        formData.append("completeTaskPercentage", 85);

        if (!taskData) {
          formData.append("project_id", projectId ? projectId : values.project);
        }

        if (values.attachments.length > 0) {
          values.attachments.forEach((attachment) => {
            if (attachment.file) {
              formData.append('images', attachment.file);
            }
          });
        }

        if (taskData) {
          // Ensure backend receives deleted attachment ids as an array
          deletedAttachments.forEach((id) => {
            formData.append('deleted_images[]', id);
          });
          
          await updateTask(taskData.id, formData);
          toast.success("Task updated successfully");
        } else {
          await CreateTask(formData);
          toast.success("Task created successfully");
        }

        setSuccessbarVisible(true);
        resetForm();
        setAttachments([]);
        onSuccess?.();
      } catch (error) {
        console.error("Error creating/updating task:", error);
        toast.error("Error creating/updating task");
      } finally {
        setIsLoading(false);
      }
    },
  });

  const canUseAttachments = () => {
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };
    return planLevels[currentPlan] >= planLevels[FEATURE_REQUIRED_PLANS.attachments];
  };

  const handleAddAttachment = () => {
    if (!canUseAttachments()) {
      setShowUpgradeModal(true);
      return;
    }
    const newAttachment = { id: Date.now(), file: "" };
    setAttachments((prev) => [...prev, newAttachment]);
    formik.setFieldValue("attachments", [
      ...formik.values.attachments,
      newAttachment,
    ]);
  };

  const handleRemoveAttachment = (id) => {
    const updatedAttachments = attachments.filter(
      (attachment) => attachment.id !== id
    );
    setAttachments(updatedAttachments);
    formik.setFieldValue(
      "attachments",
      formik.values.attachments.filter((attachment) => attachment.id !== id)
    );
  };

  const handleFileChange = (id, file) => {
    const updatedAttachments = attachments.map((attachment) =>
      attachment.id === id ? { ...attachment, file } : attachment
    );
    setAttachments(updatedAttachments);
    formik.setFieldValue("attachments", updatedAttachments);
  };

  const handleRemoveExistingAttachment = (id) => {
    setExistingAttachments(prev => prev.filter(att => att.id !== id));
    setDeletedAttachments(prev => [...prev, id]);
  };

  const handleClose = () => {
    formik.resetForm();
    setAttachments([]);
    setExistingAttachments([]);
    setDeletedAttachments([]);
    setSelectedProject(null);
    setSelectedProjectName("");
    onClose();
  };

  const resetForm = () => {
    formik.resetForm();
    setAttachments([]);
    setExistingAttachments([]);
    setDeletedAttachments([]);
    if (!projectId) {
      setSelectedProject(null);
      setSelectedProjectName("");
    }
  };

  if (!visible) return null;
  return (
    <div className="fixed inset-0 z-50" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/30"></div>
      <div className="absolute top-0 right-0 w-[400px] h-full bg-white shadow-lg flex flex-col" onClick={(e)=>e.stopPropagation()}>
      <div className="flex justify-between items-center p-4 border-b">
        <h2 className="text-[20px] font-semibold text-[#1E3A8A]">
          {taskData ? 'Update Task' : 'Create Task'}
        </h2>
        <button className="text-gray-500 hover:text-gray-700" onClick={handleClose}>
          {SimpleCross}
        </button>
      </div>

      <form onSubmit={formik.handleSubmit} className="flex flex-col h-[calc(100%-140px)]">
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {projectId ? (
            <div className="flex flex-col gap-2">
              <p className="font-normal text-[14px] text-[#4B5563]">Project</p>
              <input
                type="text"
                value={selectedProjectName?.title || "Loading..."}
                placeholder="Project name"
                disabled
                className="w-full h-[52px] px-3 border rounded bg-gray-50 text-gray-700 placeholder-gray-400 cursor-not-allowed"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <p className="font-normal text-[14px] text-[#4B5563]">Project</p>
              <Select
                placeholder="Project name"
                value={formik.values.project}
                onChange={(value) => formik.setFieldValue("project", value)}
                className={`w-full h-[44px] rounded-[4px] border ${
                  formik.touched.project && formik.errors.project
                    ? "border-red-500"
                    : "border-[#E9E9E9]"
                }`}
                suffixIcon={DropDown}
              >
                {projects.map((project) => (
                  <Option key={project.id} value={project.id}>
                    {project.title}
                  </Option>
                ))}
              </Select>
              {formik.touched.project && formik.errors.project && (
                <p className="text-red-500 text-sm">{formik.errors.project}</p>
              )}
            </div>
          )}

          <input
            type="text"
            name="taskName"
            placeholder="Task name"
            className={`w-full h-[52px] px-3  border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${
              formik.touched.taskName && formik.errors.taskName
                ? "border-red-500"
                : "border-gray-300"
            }`}
            value={formik.values.taskName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.taskName && formik.errors.taskName && (
            <p className="text-red-500 text-sm">{formik.errors.taskName}</p>
          )}

          <textarea
            name="description"
            placeholder="Description"
            className={`w-full px-3 py-2  mt-2 h-[156px] rounded-[4px] bg-white border focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${
              formik.touched.description && formik.errors.description
                ? "border-red-500"
                : "border-gray-300"
            }`}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          ></textarea>
          {formik.touched.description && formik.errors.description && (
            <p className="text-red-500 text-sm">{formik.errors.description}</p>
          )}

          <div className="flex flex-col gap-2">
            <p className="font-normal text-[14px] text-[#4B5563] mb-2">
              Attachment
              {!canUseAttachments() && (
                <span className="text-gray-400 text-xs ml-2">(Available in Basic plan and above)</span>
              )}
            </p>

            {canUseAttachments() && existingAttachments.map((attachment) => (
              <div key={attachment.id} className="flex items-start gap-2 mb-2">
                <div className="flex-1 min-w-0 border rounded p-2 flex items-center">
                  {attachment.type.startsWith('image/') ? (
                    <img 
                      src={`${IMAGE_BASE_URL}${attachment.url}`}
                      alt={attachment.original_name}
                      className="h-12 w-12 object-cover mr-2 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-12 w-12 flex items-center justify-center bg-gray-100 mr-2 flex-shrink-0">
                      <span>File</span>
                    </div>
                  )}
                  <span className="text-sm break-words min-w-0">{attachment.original_name}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveExistingAttachment(attachment.id)}
                  className="text-red-500 hover:text-red-700 text-xl flex-shrink-0"
                >
                  ×
                </button>
              </div>
            ))}

            {canUseAttachments() && attachments.map((attachment) => (
              <div key={attachment.id} className="flex items-center gap-2 mb-2">
                <input
                  type="file"
                  onChange={(e) => handleFileChange(attachment.id, e.target.files[0])}
                  className={`w-full h-[60px] p-2 border rounded focus:outline-none ${
                    formik.touched.attachments && formik.errors.attachments
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                <button
                  onClick={() => handleRemoveAttachment(attachment.id)}
                  type="button"
                  className="text-red-500 hover:text-red-700 text-xl"
                >
                  ×
                </button>
              </div>
            ))}

            <button
              onClick={handleAddAttachment}
              type="button"
              className={`flex items-center justify-center w-[60px] h-[60px] rounded-[4px] bg-white border-0 focus:outline-none focus:ring-0 
                ${!canUseAttachments() ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {SimplePlus}
            </button>

            {formik.errors.attachments && typeof formik.errors.attachments === "string" && (
              <p className="text-red-500 text-sm">{formik.errors.attachments}</p>
            )}
          </div>
        </div>
      </form>

      <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
        <button
          type="submit"
          onClick={formik.handleSubmit}
          className="bg-[#F97316] hover:bg-orange-600 w-[152px] text-white py-2 px-4 rounded"
        >
          {isLoading ? <ButtonLoader /> : taskData ? "Update" : "Create Task"}
        </button>
      </div>

      <SuccessSideBar
        visible={isSuccessbarVisible}
        onClose={() => {
          setSuccessbarVisible(false);
          resetForm();
        }}
        closeCreate={handleClose}
        from={from}
        resetForm={resetForm}
      />
      
      <UpgradePromptModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        featureName="Task Attachments"
        requiredPlan="Basic"
      />
      </div>
    </div>
  );
};

export default CreateTaskSidebar;
